// Globals
import { useTranslation } from 'react-i18next';
import { ValidateErrorEntity } from 'rc-field-form/lib/interface';

// Components
import {
  StyledPageWrapper,
  StyledTitle,
  StyledForm,
  StyledButton,
  StyledLinkBlock,
  StyledFeedLink,
} from './CreateCategoryFormStyles';
import CategoryNameSwitch from '../../components/CategoryNameSwitch/CategoryNameSwitch';
import ParentCategorySelect from '../ParentCategorySelect/ParentCategorySelect';
import CreateCategoryPhotoField from '../CreateCategoryPhotoField/CreateCategoryPhotoField';
import { FormItem, IFormInstance } from '@/components';

// Models
import { CategoryLocaleCodes, ICategory } from '@/models/categories.model';

// Helpers
import { CreateCategoryFieldNames } from '../../helpers/types';
import { useCreateCategoryValidationRules } from '../../hooks/useCreateCategoryValidationRules';
import { Input } from '@/ui';
import { CategoryIsHeavyField } from '../CategoryIsHeavyField';

export const CREATE_CATEGORY_FORM_NAME = 'CREATE_CATEGORY_FORM_NAME';

const initialLocale = [
  { locale: CategoryLocaleCodes.UK, name: '' },
  { locale: CategoryLocaleCodes.RU, name: '' },
];

type TCreateCategoryForm = {
  formInstance: IFormInstance;
  onSubmit: () => void;
  isLoading: boolean;
  categories: ICategory[] | null;
  onFinishFailed: (error: ValidateErrorEntity) => void;
};

const CreateCategoryForm = ({ formInstance, onSubmit, isLoading, categories, onFinishFailed }: TCreateCategoryForm) => {
  const { t } = useTranslation(['categories']);

  const rules = useCreateCategoryValidationRules();

  return (
    <StyledPageWrapper>
      <StyledForm
        initialValues={{
          locale: initialLocale,
        }}
        onFinish={onSubmit}
        onFinishFailed={onFinishFailed}
        form={formInstance}
        name={CREATE_CATEGORY_FORM_NAME}
      >
        <StyledTitle>{t('create_category_title')}</StyledTitle>

        <CategoryNameSwitch
          label={t('category_name')}
          formItemName={CreateCategoryFieldNames.LOCALE}
          locale={initialLocale}
          validationRules={rules[CreateCategoryFieldNames.LOCALE]}
        />

        <FormItem
          name={CreateCategoryFieldNames.GOOGLE_FEED_ID}
          label={t('category_google_feed_id')}
          rules={rules[CreateCategoryFieldNames.GOOGLE_FEED_ID]}
        >
          <Input />
        </FormItem>
        <StyledLinkBlock>
          <span>{t('category_google_feed_id_text')}</span>&nbsp;
          <StyledFeedLink to={'https://support.google.com/merchants/answer/6324436?hl=uk'} target="_blank">
            {t('category_redirect')}
          </StyledFeedLink>
        </StyledLinkBlock>

        <CategoryIsHeavyField fieldName={CreateCategoryFieldNames.IS_HEAVY} />

        <ParentCategorySelect categories={categories} formItemName={CreateCategoryFieldNames.PARENT_ID} />

        <CreateCategoryPhotoField />

        <StyledButton type="primary" htmlType="submit" loading={isLoading}>
          {t('create_category_confirm')}
        </StyledButton>
      </StyledForm>
    </StyledPageWrapper>
  );
};

export default CreateCategoryForm;
